<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import {
  NATIVE_IMPORT_FIELDS,
  NEW_FIELD_TARGET,
  IGNORE_TARGET,
} from 'dashboard/helper/contactImportHelper';

const props = defineProps({
  rows: { type: Array, required: true },
  contactAttributes: { type: Array, default: () => [] },
  rowErrors: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:rows']);

const { t } = useI18n();

const MAPPING_KEY = 'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING';

const fieldOptions = computed(() =>
  NATIVE_IMPORT_FIELDS.map(field => ({
    value: `field:${field}`,
    label: t(`${MAPPING_KEY}.FIELDS.${field.toUpperCase()}`),
  }))
);

const attributeOptions = computed(() =>
  props.contactAttributes.map(attribute => ({
    value: `attr:${attribute.attributeKey}`,
    label: attribute.attributeDisplayName || attribute.attributeKey,
  }))
);

const updateRow = (index, changes) => {
  emit(
    'update:rows',
    props.rows.map(row => (row.index === index ? { ...row, ...changes } : row))
  );
};
</script>

<template>
  <div
    class="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-x-4 gap-y-3 items-start text-sm"
  >
    <span class="text-xs font-medium text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.COLUMN') }}
    </span>
    <span class="text-xs font-medium text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.EXAMPLE') }}
    </span>
    <span class="text-xs font-medium text-n-slate-11">
      {{
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.DESTINATION')
      }}
    </span>

    <template v-for="row in rows" :key="row.index">
      <span
        class="py-2 font-medium truncate text-n-slate-12"
        :title="row.header"
      >
        {{
          row.header.trim() ||
          t(
            'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.EMPTY_HEADER'
          )
        }}
      </span>
      <span class="py-2 truncate text-n-slate-11" :title="row.example">
        {{ row.example }}
      </span>
      <div class="flex flex-col gap-1">
        <select
          :value="row.target"
          :disabled="disabled"
          :data-testid="`mapping-target-${row.index}`"
          class="w-full px-3 py-2 text-sm border rounded-lg reset-base bg-white dark:bg-slate-900 text-n-slate-12"
          :class="rowErrors[row.index] ? 'border-n-ruby-9' : 'border-n-weak'"
          @change="updateRow(row.index, { target: $event.target.value })"
        >
          <optgroup
            :label="
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.GROUP_FIELDS'
              )
            "
          >
            <option
              v-for="option in fieldOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </optgroup>
          <optgroup
            v-if="attributeOptions.length"
            :label="
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.GROUP_ATTRIBUTES'
              )
            "
          >
            <option
              v-for="option in attributeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </optgroup>
          <optgroup
            :label="
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.GROUP_OTHER'
              )
            "
          >
            <option :value="NEW_FIELD_TARGET">
              {{
                t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.NEW_FIELD'
                )
              }}
            </option>
            <option :value="IGNORE_TARGET">
              {{
                t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.IGNORE'
                )
              }}
            </option>
          </optgroup>
        </select>
        <Input
          v-if="row.target === NEW_FIELD_TARGET"
          :model-value="row.newFieldName"
          :placeholder="
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.NEW_FIELD_PLACEHOLDER'
            )
          "
          :disabled="disabled"
          @update:model-value="updateRow(row.index, { newFieldName: $event })"
        />
        <span v-if="rowErrors[row.index]" class="text-xs text-n-ruby-11">
          {{ t(`${MAPPING_KEY}.ERRORS.${rowErrors[row.index]}`) }}
        </span>
      </div>
    </template>
  </div>
</template>
